import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ItemToReturn } from '@src/models/returns';
import { useSummaryStore } from '@src/stores/summaryStore';
import { SkeletonLine } from '@one-thd/sui-atomic-components';
import {
  persistReturnSummaryinfo,
  buildInStoreReturnSummaryData,
} from '@src/utils/finalizeReturnUtil';
import useMultiCallCreateReturn from '@src/hooks/useMultiCallCreateReturn';
import { useProcessValidator } from '@src/hooks/useProcessValidator';
import { useItemEditRemove } from '@src/hooks/useItemEditRemove';
import { useRefundCalculator } from '@src/hooks/useRefundCalculator';
import { useShipmentSeparation } from '@src/hooks/useShipmentSeparation';
import { TagOnConfirmReturn } from '@src/utils/analytics-tagging';
import { useReturnStore } from '../../stores/returnStore';
import ReturnsLoadingFlow from './ReturnsLoadingFlow';
import { RETURN_METHOD } from '../../constants/general';
import { ReturnButtons } from '../shared/ReturnButtons';
import { ReturnHeaders } from '../shared/ReturnHeaders';
import { RefundTotal } from '../shared/RefundTotal';
import { goToPreviousStep } from '../../stores/newFlowStore';
import FinalizeReturnError from '../finalize-return/FinalizeReturnError';
import FraudModal from '../shared/fraud/FraudModal';
import ErrorBanner from '../shared/ErrorBanner';
import RemoveItemModal from './modal/RemoveItemModal';
import { ReturnCreateErrorStep } from './ReturnCreateErrorStep';
import { ReturnMethodCards } from './ReturnMethodCards';

const SUBMISSION_STEPS = {
  UNSUBMITTED: 'unsubmitted',
  SUBMITTING: 'submitting',
  ERROR: 'returnCreateError',
} as const;

type SubmissionStep = (typeof SUBMISSION_STEPS)[keyof typeof SUBMISSION_STEPS];

const ReviewReturnDetailsStep = () => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const refundValues = useSummaryStore((state) => state.refundValues);
  const returnShipments = useReturnStore((state) => state.returnShipments);
  const orderDetailsPath = useReturnStore((state) => state.orderDetailsPath);
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const navigate = useNavigate();
  const [submissionStep, setSubmissionStep] = useState<SubmissionStep>(
    SUBMISSION_STEPS.UNSUBMITTED,
  );
  const [showLorpCheckboxError, setShowLorpCheckboxError] = useState(false);
  const { isRefundCalculatorLoading, calculateRefund } = useRefundCalculator({
    orderDetailsV2,
  });

  // Callback used when policy validator is successful
  const recalculateRefund = (modifiedSelectedItems?: ItemToReturn[]) =>
    calculateRefund(modifiedSelectedItems || selectedItems);

  const {
    processPolicyValidator,
    renderModal,
    returnFraudType,
    policyValidatorAlert,
    setPolicyValidatorAlert,
    isPolicyValidatorLoading,
  } = useProcessValidator({
    orderDetailsV2,
    returnEligibilityDetails,
    isMilitaryExchangeGuest,
    proceedToNextStep: recalculateRefund,
  });

  const {
    onEditItemSubmit,
    onRemoveItemSubmit,
    onConfirmRemoveItem,
    onCancelRemoveItem,
    renderRemoveItemModal,
  } = useItemEditRemove({
    selectedItems,
    processPolicyValidator,
    setPolicyValidatorAlert,
    proceedToNextStep: recalculateRefund,
    orderDetailsPath,
    isMilitaryExchangeGuest,
  });

  const { lorpShipments } = useShipmentSeparation(returnShipments);

  const { APICallState, makeCreateReturnCalls, apiError, isLoading } =
    useMultiCallCreateReturn();

  const militaryExchange = useReturnStore((state) => state.militaryExchange);
  const tenders = useReturnStore((state) => state.tenders);

  // Helper function to check if there are unagreed LORP shipments
  const hasUnagreedLorpShipments = () =>
    lorpShipments?.some((shipment) => !shipment.hasAgreed) ?? false;

  // Clear error when all LORP shipments are agreed
  useEffect(() => {
    if (!hasUnagreedLorpShipments() && showLorpCheckboxError) {
      setShowLorpCheckboxError(false);
    }
  }, [lorpShipments, showLorpCheckboxError]);

  const onButtonContinueClick = () => {
    // Check if there are unagreed LORP shipments
    if (hasUnagreedLorpShipments()) {
      setShowLorpCheckboxError(true);
      return;
    }

    TagOnConfirmReturn(
      selectedItems,
      militaryExchange,
      tenders?.[0]?.type,
      orderDetailsV2,
    );
    const hasAllInStoreItems = selectedItems.every(
      (item) => item.returnMethod === RETURN_METHOD.IN_STORE,
    );

    // If all items are being returned in-store, skip the return creation call and navigate directly to the return summary page
    if (hasAllInStoreItems && returnShipments) {
      const returnSummaryData = buildInStoreReturnSummaryData(
        orderDetailsV2?.orderNumber || '',
        orderDetailsV2?.emailId || '',
        selectedItems,
        refundValues,
        returnShipments,
      );
      persistReturnSummaryinfo(returnSummaryData, navigate);
      return;
    }

    makeCreateReturnCalls();

    // Only show skeleton loader for LORP returns
    const hasLorpItems = lorpShipments && lorpShipments.length > 0;
    if (hasLorpItems) {
      setSubmissionStep(SUBMISSION_STEPS.SUBMITTING);
    }
  };

  const onRetryFailedCalls = () => {
    // Show skeleton loader during retry
    setSubmissionStep(SUBMISSION_STEPS.SUBMITTING);

    // Retry the calls - this will continue from currentStep which is already positioned
    // after successful calls, and completedCalls already has successful ones preserved
    makeCreateReturnCalls();
  };

  useEffect(() => {
    if (
      !isLoading &&
      submissionStep === SUBMISSION_STEPS.SUBMITTING &&
      apiError !== null
    ) {
      // Handle failed submission
      setSubmissionStep(SUBMISSION_STEPS.ERROR);
    }
  }, [isLoading, submissionStep, apiError]);

  const renderUnsubmittedState = () => (
    <>
      {apiError && <FinalizeReturnError error={apiError} />}
      {policyValidatorAlert && <ErrorBanner />}
      <ReturnMethodCards
        onEditItemSubmit={onEditItemSubmit}
        onRemoveItemSubmit={onRemoveItemSubmit}
        isPolicyValidatorLoading={isPolicyValidatorLoading}
        showLorpCheckboxError={showLorpCheckboxError}
      />
      {isRefundCalculatorLoading ? (
        <div className="sui-mt-4" data-testid="method-skeleton">
          <SkeletonLine variant="multi" numberOfLines={2} />
        </div>
      ) : (
        <div className="sui-mt-8">
          <RefundTotal />
        </div>
      )}
      <ReturnButtons
        proceedBack={goToPreviousStep}
        proceedNext={onButtonContinueClick}
        loading={isLoading}
        final
      />
      {renderModal && (
        <FraudModal
          returnFraudType={returnFraudType}
          title={renderModal.title}
          body={renderModal.bodyMessage}
          buttonText={renderModal.buttonMessage}
          onClick={renderModal.onClick}
        />
      )}
      {renderRemoveItemModal && (
        <RemoveItemModal
          onConfirmRemove={onConfirmRemoveItem}
          onCancelRemove={onCancelRemoveItem}
        />
      )}
    </>
  );
  return (
    <div className="sui-max-w-[555px]">
      <ReturnHeaders title="Review the items below" />
      {submissionStep === SUBMISSION_STEPS.UNSUBMITTED &&
        renderUnsubmittedState()}
      {submissionStep === SUBMISSION_STEPS.SUBMITTING && (
        <ReturnsLoadingFlow apiCallState={APICallState} />
      )}
      {submissionStep === SUBMISSION_STEPS.ERROR && (
        <ReturnCreateErrorStep
          APICallStateProp={APICallState}
          onRetry={onRetryFailedCalls}
        />
      )}
    </div>
  );
};

export default ReviewReturnDetailsStep;
